@file:UseSerializers(BigDecimalSerializer::class)

package net.sergeych.ugdx.shared

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import net.sergeych.crypto2.SealedBox
import net.sergeych.kryptools.randomId
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.shared.api.ApiAccount
import net.sergeych.ugdx.shared.api.SubmitOrderArgs
import net.sergeych.ugdx.shared.api.cmdSubmitOrder
import net.sergeych.ugdx.shared.api.invoke

/**
 * Order is a signable instruction to u:asss service to perform some potentially dangerous action, such
 * as transfering/exporting funds, etc. The procedure is:
 *
 * - obtain the necessary user from [net.sergeych.ugdx.UserAccount]
 * - show it to the user for review.
 * - if the user accept it, execute it. Ti will sign the order by the current user signature, anf might ask teh password
 *   as usual. Signed order will be automatically passed to the service for execution. Order could only be executed
 *   once.
 * - otherwise, order could be cancelled by the user - but only _before it was accepted_. Accepted order revokation
 *   is a separate procedure, and in most cases impossible.
 *
 *   The parameters of the order are used internally by the system and should not be altered.
 */
@Suppress("unused")
@Serializable
sealed class Order(
    /**
     * Creation timestamp
     */
    val createdAt: Instant = Clock.System.now(),
    /**
     * Used only to make signed order globally unique to prevent misuse or occasional duplication.
     */
    val uniqueId: String = randomId(47),
) {

    /**
     * Order status is reported with [OrderRecord], [OrderRecord.refresh] to get its current state.
     * @param isFinal if true, order status will not be changed anymore and could be cached/stored. Otherwise,
     *                refreshing order status may return different status.
     */
    @Serializable
    sealed class Status(val isFinal: Boolean) {
        /**
         * Orede is not signed end sent to u:ass service. Could be canceled or just disposed
         */
        @Serializable
        @SerialName("new")
        object New : Status(false)

        /**
         * Order is submitted to the u:ass service but not yet processed
         */
        @Serializable
        @SerialName("submitted")
        object Submitted : Status(false)

        /**
         * Order is executed successfully
         */
        @Serializable
        @SerialName("executed")
        object Executed : Status(true)

        /**
         * Order is rejected by the service with a message
         */
        @Serializable
        @SerialName("rejected")
        class Rejected(val message: String): Status(true)
    }

    /**
     * Call it __ONLY AFTER USER HAS READ THE ORDER DETAILS AND PROVIDE ONE'S CONSENT BY CHECKING SOME SORT OF CHECKBOX
     * AND ONLY THEN__ this method should ne called. In most cases, executing order is irreversible.
     *
     * Signs the order with current user signature (the password might be requested via state flow), and send it to
     * the service. Important note: system does not queue orders, it is either be immediately submitted, or the exception
     * will be thrown. Exception mau be general (connection errors).
     *
     * Use [OrderRecord.refresh] to check order status change unless its [OrderRecord.lastKnownStatus] is
     * [Order.Status.isFinal]
     *
     * @return a record containing also `id` assigned by the system. Subsequent attempts to submit the same order will
     *         return the same result, no new order will be created.
     */
    suspend fun submit(): OrderRecord {
        val acc = GuestAPI().userAccountOrThrow
        acc.clearPasswords()
        val signedOrder = SealedBox.encode(
            this,
            acc.mainKey(),
        )
        return cmdSubmitOrder.invoke(SubmitOrderArgs(signedOrder)).also { or ->
            if( or.order is ExchangeAccessOrder && or.lastKnownStatus == Status.Executed )
                GuestAPI().currentAccount?.let {
                    println("------ ${or.order.allowApiAccess}")
                    it.canUseExchange = or.order.allowApiAccess
                    println("now: $it")
                    println("now: ${it.canUseExchange}")
                }
        }
    }
}

/**
 * All transfer orders implements: payment data. Recipient data are different.
 */
interface TransferOrder {
    /**
     * The u:ass account that will be the payer
     */
    val payerAccountId: Long

    /**
     * transferred amount, not including any commissions. Commissions and other applicable fees are charged
     * automaticaly, but _separately_, the the [amount] will exactly be tranfered, or not transferred at all.
     */
    val amount: BigDecimal

    /**
     * Purpose of the payment. Should not be empty in the sake of any ruther accident or support operations.
     */
    val purpose: String
}

/**
 * Transfer to another user of the u:ass system, e.g. local. The receiving party only discloses the [recipientLoginName]
 * and the default account for the transferring currency will be used or created. This prevents, for example, occasional
 * topping up special accounts that may have recipient concent to use (for example, account can be a term deposit, but
 * the term deposit can not be default account and won't be used by transfer system)
 */
@Serializable
@SerialName("LocalTransferOrder")
class LocalTransferOrder(
    override val payerAccountId: Long,
    override val amount: BigDecimal,
    override val purpose: String,
    val recipientLoginName: String,
) : TransferOrder, Order()


@Serializable
@SerialName("InvoicePaymentOrder")
class InvoicePaymentOrder(
    override val payerAccountId: Long,
    override val amount: BigDecimal,
    override val purpose: String,
    @Suppress("unused") val invoiceUniqueId: String
): TransferOrder, Order()

@Serializable
@SerialName("ExchangeAccessOrder")
class ExchangeAccessOrder(
    val allowApiAccess: Boolean
): Order()


/**
 * Export to external payment system (e.g. blockchain: ethereum, bitcoin, universa), assuming than export cost is
 * charged to the [payerAccountId]. Could be extended to export to fial account, using IBAN for example.
 */
@Suppress("unused")
@Serializable
@SerialName("ExportOrder")
open class ExportOrder(
    override val payerAccountId: Long,
    override val amount: BigDecimal,
    override val purpose: String,
    val externalAddress: String,
    /**
     * Some external networs have fixed tranfer cost, some varying and even unpredictable. This is a cost that will
     * be charged for the operation, and in some cases part of it may be later returned, if the actual transfer
     * cost will be less than this one.
     *
     * Setting it to the small amount may effectively stale the export operation indefinitely.
     */
    val maximumTransferCost: BigDecimal,
) : TransferOrder, Order()

/**
 * Export to the external system, like [ExportOrder], but the transfer cost is charged to another account. It is the
 * only way to export ERC20 contracts, non-stable Universa tokens, etc.
 */
@Suppress("unused")
@Serializable
@SerialName("ExtendedExportOrder")
class ExtendedExportOrder(
    val exportOrder: ExportOrder,
    val transferCostAccount: ApiAccount,
) : Order(), TransferOrder by exportOrder

@Serializable
data class OrderRecord(
    val id: Long,
    val order: Order,
    val submittedAt: Instant,
    val lastKnownStatus: Order.Status,
    val serviceMessages: List<ServiceMessage>
) {
    suspend fun refresh(): OrderRecord = GuestAPI().getOrder(id)
}